/**
 * SimplyLoan Google Ads API client.
 *
 * GET /api/simplyloan/google-dashboard returns cohort revenue: every number on a row belongs to the
 * leads that row acquired in the selected dates, whenever the commission was earned. Money is DKK.
 * Also returns top Search Terms and supports live campaign/adgroup status, budget, and negative keyword controls.
 */
import { API_BASE_URL } from '../config';

export type GoogleGeo = 'all' | 'se' | 'no' | 'fi';
export type GoogleLevel = 'campaign' | 'adgroup' | 'ad';

export interface GoogleDashboardParams {
  geo: GoogleGeo;
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
  level: GoogleLevel;
}

export interface GoogleDashboardRow {
  key: string | null;               // campaign_id, adgroup_id or ad_id; null = Google leads without an object id
  name: string;
  campaign_id: string | null;
  campaign_name: string | null;
  adgroup_id: string | null;
  adgroup_name: string | null;
  customer_id: string | null;
  channel_type: string | null;
  bidding_strategy_type: string | null;
  geo: string | null;
  status: string | null;            // ENABLED | PAUSED | REMOVED
  policy_approval_status: string | null;
  ad_strength: string | null;
  daily_budget_account: number | null;
  spend_dkk: number;
  fx_missing: boolean;
  impressions: number;
  clicks: number;
  google_conversions: number;       // as Google Ads reports them
  google_conversions_value: number;
  search_impression_share: number | null;
  leads: number;                    // our leads (web form + gclid/gbraid/wbraid click-outs)
  rev_d0: number;                   // approved commission within 24 h of acquisition
  rev_d7: number;
  rev_d14: number;
  rev_d30: number;
  rev_d90: number;                  // to date, capped at 90 days
  rev_pending: number;
  leads_mature_d30: number;
  projected_d30: number | null;
  cpl_dkk: number | null;
  cpc_dkk: number | null;
  ctr: number | null;
  drip_revenue_dkk: number;
  immediate_roas: number | null;
  ltv30_roas: number | null;
  projected_ltv30_roas: number | null;
  roas_to_date: number | null;
}

export interface GoogleSearchTermRow {
  search_term: string;
  campaign_id: string;
  campaign_name: string;
  adgroup_id: string;
  adgroup_name: string;
  customer_id: string;
  geo: string | null;
  keyword_text: string;
  keyword_match_type: string;
  search_term_status: string;
  impressions: number;
  clicks: number;
  cpc_dkk: number | null;
  ctr: number | null;
  spend_dkk: number;
  conversions: number;
  conversions_value: number;
  cpa_dkk: number | null;
}

export interface GoogleDashboardKpis {
  spend_dkk: number;
  impressions: number;
  clicks: number;
  cpc_dkk: number | null;
  ctr: number | null;
  leads: number;
  google_reported_conversions: number;
  cpl_dkk: number | null;
  frontend_revenue_dkk: number;
  drip_revenue_dkk: number;
  total_revenue_dkk: number;
  pending_revenue_dkk: number;
  blended_roas: number | null;
  net_margin_dkk: number;
  projected_d30_revenue_dkk: number | null;
  unmapped_spend_dkk: number;
  fx_missing: boolean;
}

export interface GoogleConversionHealth {
  hours: number;
  by_status: Record<string, number>;
  by_event: Record<string, Record<string, number>>;
  attempted: number;
  error_rate: number | null;
  gclid_match_share: number | null;
  ec_match_share: number | null;
  auth_errors: number;
  backlog_over_60m: number;
}

export interface GoogleDashboardResponse {
  filters: { geo: string; level: GoogleLevel; from: string; to: string; timezone: string };
  currency: 'DKK';
  kpis: GoogleDashboardKpis;
  rows: GoogleDashboardRow[];
  search_terms: GoogleSearchTermRow[];
  d30_multipliers: Record<string, number>;
  health: { conversions_24h: GoogleConversionHealth; synced_at: Record<string, string | null> };
  generated_at: string;
}

export async function fetchGoogleDashboard(
  params: GoogleDashboardParams,
  signal?: AbortSignal,
): Promise<GoogleDashboardResponse> {
  const qs = new URLSearchParams({ geo: params.geo, from: params.from, to: params.to, level: params.level });
  const res = await fetch(`${API_BASE_URL}/api/simplyloan/google-dashboard?${qs.toString()}`, { signal });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Google Ads dashboard request failed (${res.status})${text ? `: ${text.slice(0, 200)}` : ''}`);
  }
  return res.json();
}

// ─── Campaign builder & live controls ────────────────────────────────────────

export type PlanStatus =
  | 'draft' | 'validated' | 'queued' | 'building' | 'dry_run' | 'built' | 'failed' | 'active' | 'paused';

export interface PlanFinding {
  level: 'error' | 'warning';
  field: string;
  message: string;
  match: string;
}

export interface PlanPreviewAd {
  ad_group: string;
  ad: string;
  headlines: string[];
  descriptions: string[];
  path1: string;
  path2: string;
  final_url: string | null;
  keywords_count: number;
  sample_keywords: string[];
}

export interface PlanValidation {
  ok: boolean;
  errors: PlanFinding[];
  warnings: PlanFinding[];
  preview: PlanPreviewAd[];
  daily_budget: number | null;
  currency: string | null;
}

export interface CampaignPlanRow {
  id: number;
  plan_key: string;
  geo: string;
  name: string;
  status: PlanStatus;
  validation: { ok: boolean; errors: PlanFinding[]; warnings: PlanFinding[]; daily_budget: number | null; currency: string | null } | null;
  last_error: string | null;
  created_by: string | null;
  approved_by: string | null;
  created_at: string;
  updated_at: string;
  built_at: string | null;
  activated_at: string | null;
  objects: number;
  campaign_id: string | null;
  spec: Record<string, unknown>;
}

export interface PlanListResponse {
  mode: 'dry_run' | 'live';
  activation_allowed: boolean;
  plans: CampaignPlanRow[];
}

export interface PlanTemplate {
  file: string;
  spec: Record<string, unknown>;
}

export interface PlanActionResponse extends Partial<PlanValidation> {
  status: string;
  plan_id?: number;
  reason?: string;
  activated?: number;
  paused?: number;
  created_objects?: number;
}

export interface LiveControlResponse {
  status: string;
  reason?: string;
  error?: string;
  customer_id?: string;
  object_type?: string;
  object_id?: string;
  new_status?: string;
  campaign_id?: string;
  daily_budget?: number;
  keyword?: string;
  match_type?: string;
  resource_name?: string;
}

async function campaignsRequest<T>(path: string, body?: unknown): Promise<T> {
  const init: RequestInit = body === undefined
    ? {}
    : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
  const res = await fetch(`${API_BASE_URL}/api/simplyloan/google-campaigns${path}`, init);
  const text = await res.text();
  let parsed: any = {};
  try {
    parsed = text ? JSON.parse(text) : {};
  } catch {
    parsed = { error: text.slice(0, 200) };
  }
  if (!res.ok && res.status !== 409) {
    throw new Error(parsed?.error || parsed?.reason || `Request failed (${res.status})`);
  }
  return parsed as T;
}

export const listGoogleCampaignPlans = () => campaignsRequest<PlanListResponse>('');
export const getGoogleCampaignTemplates = () => campaignsRequest<{ templates: PlanTemplate[] }>('/templates');
export const validateGoogleCampaignPlan = (plan: unknown) => campaignsRequest<PlanValidation>('/validate', { plan });
export const saveGoogleCampaignPlan = (plan: unknown) => campaignsRequest<PlanActionResponse>('', { plan });
export const googleCampaignPlanAction = (action: 'queue' | 'build-now' | 'activate' | 'pause', planId: number) =>
  campaignsRequest<PlanActionResponse>(`/${action}`, { plan_id: planId });

export const updateGoogleLiveStatus = (params: {
  geo?: string;
  customer_id?: string;
  object_type: 'campaign' | 'adgroup';
  object_id: string;
  status: 'ENABLED' | 'PAUSED';
}) => campaignsRequest<LiveControlResponse>('/live-status', params);

export const updateGoogleLiveBudget = (params: {
  geo?: string;
  customer_id?: string;
  campaign_id: string;
  daily_budget: number;
}) => campaignsRequest<LiveControlResponse>('/live-budget', params);

export const addGoogleNegativeKeyword = (params: {
  geo?: string;
  customer_id?: string;
  campaign_id?: string;
  adgroup_id?: string;
  keyword_text: string;
  match_type?: 'EXACT' | 'PHRASE' | 'BROAD';
}) => campaignsRequest<LiveControlResponse>('/negative-keyword', params);

export const triggerGoogleSyncNow = (days: number = 7) =>
  campaignsRequest<Record<string, unknown>>('/sync-now', { days });

export const triggerGoogleDispatchNow = () =>
  campaignsRequest<Record<string, unknown>>('/dispatch-now', {});
